import { Controller } from "@hotwired/stimulus"
import L from "leaflet"

// Fix Leaflet default icon paths (required for markers to display)
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
})

// data-controller="map"
// data-map-target="container"
// data-map-target="modal"
// data-map-target="modalContent"
// data-gyms='[{"id":"...","name":"...","latitude":45.123,"longitude":15.456}]'
export default class extends Controller {
  static targets = ["container", "error", "modal", "modalContent"]
  static values = { focusGymId: String }

  connect() {
    try {
      const gymsData = this.element.dataset.gyms
      this.gyms = gymsData ? JSON.parse(gymsData) : []
    } catch (e) {
      console.error("Error parsing gyms data:", e)
      this.gyms = []
    }
    this.initMap()
  }

  disconnect() {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  initMap() {
    // Initialize the map
    this.map = L.map(this.containerTarget).fitWorld()

    // Add OpenStreetMap tile layer
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap contributors'
    }).addTo(this.map)

    // Add gym markers
    this.addGymMarkers()

    // Track if we've centered the map (to avoid double-centering)
    this.mapCentered = false

    // Set up location found handler
    this.map.on('locationfound', (e) => this.onLocationFound(e))
    this.map.on('locationerror', (e) => this.onLocationError(e))

    // Wait for map to be ready
    this.map.whenReady(() => {
      // If a specific gym is requested, center on it
      if (this.hasFocusGymIdValue) {
        this.centerOnGym(this.focusGymIdValue)
      } else {
        // Otherwise, request user location
        this.requestLocationPermission()
      }
    })
  }

  addGymMarkers() {
    if (!this.gyms || !Array.isArray(this.gyms) || this.gyms.length === 0) {
      return
    }

    // Create a custom red icon for gyms (different from default blue marker)
    const gymIcon = L.divIcon({
      className: 'gym-marker-icon',
      html: `
        <div style="
          background-color: #dc2626;
          width: 30px;
          height: 30px;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          border: 3px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <div style="
            transform: rotate(45deg);
            color: white;
            font-size: 16px;
            font-weight: bold;
          ">🏋️</div>
        </div>
      `,
      iconSize: [30, 30],
      iconAnchor: [15, 30],
      popupAnchor: [0, -30]
    })

    // Add markers for each gym
    this.gyms.forEach((gym) => {
      if (gym.latitude && gym.longitude) {
        const latlng = [parseFloat(gym.latitude), parseFloat(gym.longitude)]

        const marker = L.marker(latlng, { icon: gymIcon })
          .addTo(this.map)

        // Open modal on marker click instead of popup
        marker.on('click', () => {
          this.openGymModal(gym.id)
        })
      }
    })
  }

  onLocationFound(e) {
    const radius = e.accuracy
    const lat = e.latlng.lat
    const lng = e.latlng.lng

    // Mark that we've centered the map
    this.mapCentered = true

    // Note: setView: true in locate() already centered the map, but we can adjust zoom if needed
    if (this.map.getZoom() < 16) {
      this.map.setZoom(16)
    }

    // Add marker at user's location
    L.marker(e.latlng).addTo(this.map)
      .bindPopup(`Your location`)
      .openPopup()

    // Add circle showing accuracy
    L.circle(e.latlng, radius).addTo(this.map)

    // Save user's position to database
    this.saveUserPosition(lat, lng)
  }

  saveUserPosition(latitude, longitude) {
    // Save user position to backend for distance calculations
    fetch('/map/update_position', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      },
      body: JSON.stringify({
        latitude: latitude,
        longitude: longitude
      })
    }).catch(error => {
      console.warn('Failed to save user position:', error)
      // Non-critical error, continue silently
    })
  }

  onLocationError(e) {
    // Handle different types of location errors
    const errorCode = e.code || 0
    let errorMessage = "Unable to get your location"

    switch (errorCode) {
      case 1: // PERMISSION_DENIED
        errorMessage = "Location permission denied. Please enable location access in your browser settings."
        break
      case 2: // POSITION_UNAVAILABLE
        errorMessage = "Location unavailable. GPS signal may be weak or unavailable."
        break
      case 3: // TIMEOUT
        errorMessage = "Location request timed out. Please try again."
        break
      default:
        errorMessage = e.message || "Unable to get your location"
    }

    // Log the error for debugging
    console.log("Location unavailable:", errorMessage, "Code:", errorCode)

    // Only center on gyms if we haven't already centered
    if (!this.mapCentered) {
      this.centerOnGyms()
    }

    // Show a subtle info message (not an error) if error target exists
    // This is informational - the map still works by showing gyms
    if (this.hasErrorTarget) {
      const span = this.errorTarget.querySelector('span')
      if (span) {
        // Change to info style instead of error (less alarming)
        this.errorTarget.classList.remove("alert-error")
        this.errorTarget.classList.add("alert-info")
        span.textContent = "Showing gym locations. Your location is unavailable."
      }
      // Auto-hide after 5 seconds
      setTimeout(() => {
        if (this.hasErrorTarget) {
          this.errorTarget.classList.add("hidden")
        }
      }, 5000)
    }
  }

  async requestLocationPermission() {
    // Check if Permissions API is available (modern browsers)
    if (navigator.permissions && navigator.permissions.query) {
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'geolocation' })
        
        // If permission is already granted or prompt, proceed with location request
        if (permissionStatus.state === 'granted' || permissionStatus.state === 'prompt') {
          this.requestLocation()
        } else if (permissionStatus.state === 'denied') {
          // Permission was denied, show message and center on gyms
          console.log("Location permission denied")
          this.centerOnGyms()
        }
        
        // Listen for permission changes
        permissionStatus.onchange = () => {
          if (permissionStatus.state === 'granted') {
            this.requestLocation()
          }
        }
      } catch (error) {
        // Permissions API not supported or failed, fall back to direct request
        console.log("Permissions API not available, requesting location directly")
        this.requestLocation()
      }
    } else {
      // Permissions API not available, request location directly
      // This will trigger the browser's permission prompt
      this.requestLocation()
    }
  }

  requestLocation() {
    // Request user's location - setView: true will center on location when found
    // If location fails, the error handler will center on gyms instead
    this.map.locate({
      setView: true,
      maxZoom: 16,
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 60000 // Use cached location if less than 1 minute old
    })
  }

  centerOnGym(gymId) {
    if (!this.gyms || this.gyms.length === 0) {
      return
    }

    // Find the gym by ID (handle both string and UUID formats)
    const gym = this.gyms.find(g => {
      const gId = String(g.id || '')
      const searchId = String(gymId || '')
      return gId === searchId || gId.toLowerCase() === searchId.toLowerCase()
    })
    
    if (!gym || !gym.latitude || !gym.longitude) {
      // Gym not found or has no coordinates, fall back to centering on all gyms
      this.centerOnGyms()
      return
    }

    // Mark that we've centered the map
    this.mapCentered = true

    // Center map on the specific gym
    const latlng = [parseFloat(gym.latitude), parseFloat(gym.longitude)]
    this.map.setView(latlng, 16)

    // Open the gym modal to show details
    setTimeout(() => {
      this.openGymModal(gym.id || gymId)
    }, 300) // Small delay to ensure map has centered
  }

  centerOnGyms() {
    if (!this.gyms || this.gyms.length === 0) {
      return
    }

    // Mark that we've centered the map
    this.mapCentered = true

    // Get all gym coordinates
    const gymCoords = this.gyms
      .filter(gym => gym.latitude && gym.longitude)
      .map(gym => [parseFloat(gym.latitude), parseFloat(gym.longitude)])

    if (gymCoords.length === 0) {
      return
    }

    // If only one gym, center on it
    if (gymCoords.length === 1) {
      this.map.setView(gymCoords[0], 15)
      return
    }

    // If multiple gyms, fit bounds to show all
    const bounds = L.latLngBounds(gymCoords)
    this.map.fitBounds(bounds, { padding: [50, 50], maxZoom: 16 })
  }

  openGymModal(gymId) {
    if (!this.hasModalTarget || !this.hasModalContentTarget) {
      console.error('Modal targets not found')
      return
    }

    // Show loading state
    this.modalContentTarget.innerHTML = '<div class="flex items-center justify-center p-8"><span class="loading loading-spinner loading-lg"></span></div>'

    // Open modal
    this.modalTarget.showModal()

    // Fetch gym details via Turbo
    const url = `/map/gym/${gymId}`
    console.log('Fetching from URL:', url)

    fetch(url, {
      headers: {
        'Accept': 'text/html, application/xhtml+xml',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin'
    })
      .then(response => {
        console.log('Response status:', response.status)
        if (!response.ok) {
          throw new Error(`Failed to load gym details: ${response.status}`)
        }
        return response.text()
      })
      .then(html => {
        console.log('Received HTML, updating modal content')
        // Update modal content with the partial
        this.modalContentTarget.innerHTML = html
      })
      .catch(error => {
        console.error('Error loading gym details:', error)
        this.modalContentTarget.innerHTML = '<div class="alert alert-error"><span>Failed to load gym details. Please try again.</span></div>'
      })
  }
}
